import {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {formatPurchaseAssetNames} from '@/features/buyers/utils';

import {useBuyersQuery} from './useBuyersQuery';
import {usePurchaseBuyers} from './usePurchaseBuyers';

import type {BuyerDTO, BuyerPurchaseDTO} from '@sollapay/types';

export interface AssetPickerResult {
  assetOptions: {value: string; label: string}[];
  selectedAssetId: string;
  defaultAssetId: string;
  onAssetChange: (assetId: string) => void;
  selectedPurchase: BuyerPurchaseDTO | undefined;
  buyers: BuyerDTO[];
  reset: () => void;
}

/** `enabled` gates the queries — pass false until the picker is actually shown. */
export function useAssetPicker(trustAccountId: string, enabled: boolean): AssetPickerResult {
  const {t} = useTranslation('trustAccounts');
  const [selectedAssetId, setSelectedAssetId] = useState('');

  const {data: allBuyers = []} = useBuyersQuery(enabled ? trustAccountId : '');

  const assetOptions = useMemo(() => {
    if (!enabled) return [];
    const seen = new Set<string>();
    const options: {value: string; label: string}[] = [];
    for (const buyer of allBuyers) {
      for (const purchase of buyer.purchases) {
        if (seen.has(purchase.id)) continue;
        seen.add(purchase.id);
        options.push({value: purchase.id, label: formatPurchaseAssetNames(purchase.assets, t)});
      }
    }
    return options;
  }, [enabled, allBuyers, t]);

  const selectedPurchase = useMemo(
    () => allBuyers.flatMap(b => b.purchases).find(p => p.id === selectedAssetId),
    [allBuyers, selectedAssetId]
  );

  const {buyers} = usePurchaseBuyers(
    trustAccountId,
    enabled ? selectedAssetId || undefined : undefined
  );

  const onAssetChange = useCallback((assetId: string) => setSelectedAssetId(assetId), []);
  const reset = useCallback(() => setSelectedAssetId(''), []);

  const defaultAssetId = assetOptions.length === 1 ? (assetOptions[0]?.value ?? '') : '';

  useEffect(() => {
    if (defaultAssetId && !selectedAssetId) setSelectedAssetId(defaultAssetId);
  }, [defaultAssetId, selectedAssetId]);

  return {
    assetOptions,
    selectedAssetId,
    defaultAssetId,
    onAssetChange,
    selectedPurchase,
    buyers,
    reset
  };
}
